import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useSidebar } from "../../context/SidebarContext";
import usePermissions from "../../hooks/usePermissions";
import {
  LayoutDashboard,
  UserCircle,
  Package,
  FileText,
  DollarSign,
  Settings,
  ChevronDown,
  Lock,
  User,
  Shapes,
  ClipboardList,
  File,
  MessageSquareText,
  Star,
} from "lucide-react";

// --- ✅ Icône personnalisée : Gestion des accès / rôles ---
const AccessRoleIcon = ({ className = "" }) => (
  <div className={`relative ${className}`}>
    <Lock className="w-5 h-5" />
    <div className="absolute -bottom-1 -right-1 w-3.5 h-3.5 bg-white rounded-full flex items-center justify-center border border-gray-300">
      <User className="w-2 h-2 text-gray-700" />
    </div>
  </div>
);

// --- ✅ Liste des éléments du menu ---
// Chaque item porte la permission requise pour être visible.
const navItems = [
  {
    icon: <LayoutDashboard />,
    name: "Tableau de bord",
    path: "/admin-dashboard",
    permission: "dashboard:read",
  },
  {
    icon: <UserCircle />,
    name: "Gestion des utilisateurs",
    subItems: [
      { name: "Vendeurs", path: "/admin-dashboard/vendors", permission: "vendors:read" },
      { name: "Clients", path: "/admin-dashboard/users", permission: "clients:read" },
    ],
  },
  {
    icon: <Package />,
    name: "Validation des produits",
    path: "/admin-dashboard/products",
    permission: "products-validation:read",
  },
  {
    icon: <Star />,
    name: "Modération des avis",
    path: "/admin-dashboard/reviews",
    permission: "reviews-moderation:read",
  },
  {
    icon: <Shapes />,
    name: "Gestion des catégories",
    path: "/admin-dashboard/categories",
    permission: "categories:read",
  },
  {
    icon: <File />,
    name: "Documents généraux",
    path: "/admin-dashboard/documents-generale",
    permission: "documents-generale:read",
  },
  {
    icon: <ClipboardList />,
    name: "Toutes les commandes",
    path: "/admin-dashboard/all-orders",
    permission: "all-orders:read",
  },
  {
    icon: <FileText />,
    name: "Contenus & CMS",
    subItems: [
      { name: "Slider d'accueil", path: "/admin-dashboard/cms/slider", permission: "cms-slider:read" },
      { name: "Bannière promo", path: "/admin-dashboard/cms/banner", permission: "cms-banner:read" },
    ],
  },
  {
    icon: <DollarSign />,
    name: "Finances",
    subItems: [
      // TODO(rappel): réactiver « Paiement vendeur » — masqué temporairement
      // { name: "Paiement vendeur", path: "/admin-dashboard/finances/payments", permission: "payments:read" },
      { name: "Commission", path: "/admin-dashboard/finances/commissions", permission: "commissions:read" },
    ],
  },
  {
    icon: <MessageSquareText />,
    name: "Messages FAQ",
    path: "/admin-dashboard/faq-messages",
    permission: "faq-messages:read",
  },
  {
    icon: <AccessRoleIcon className="menu-item-icon-size" />,
    name: "Gestion des accès / rôles",
    path: "/admin-dashboard/access-roles",
    permission: "access-roles:read",
  },
  {
    icon: <Package />,
    name: "Stock / Inventaire",
    path: "/admin-dashboard/stock-maintenance",
    permission: "settings:read",
  },
  {
    icon: <Settings />,
    name: "Mon compte",
    path: "/admin-dashboard/settings",
    permission: "settings:read",
  },
];
const AdminSidebar = () => {
  const { isExpanded, isMobileOpen, isHovered, setIsHovered } = useSidebar();
  const location = useLocation();
  const { can, isLoading } = usePermissions();
  const [openSubmenu, setOpenSubmenu] = useState(null);
  const subMenuRefs = useRef({});
  const [subMenuHeight, setSubMenuHeight] = useState({});

  // Filtre la navigation selon les permissions de l'utilisateur connecté
  const visibleNavItems = useMemo(() => {
    return navItems
      .map((nav) => {
        if (nav.subItems) {
          const allowedSubs = nav.subItems.filter(
            (s) => !s.permission || can(s.permission)
          );
          if (allowedSubs.length === 0) return null;
          return { ...nav, subItems: allowedSubs };
        }
        if (nav.permission && !can(nav.permission)) return null;
        return nav;
      })
      .filter(Boolean);
  }, [can]);

  const isActive = useCallback(
    (path) => {
      // Correspondance exacte
      if (location.pathname === path) return true;
      // Pour /admin-dashboard, correspondance exacte uniquement (pas de sous-routes)
      if (path === '/admin-dashboard') return false;
      // Correspondance pour les sous-routes (ex: /admin-dashboard/products/casquette-unisexe)
      if (location.pathname.startsWith(path + '/')) return true;
      return false;
    },
    [location.pathname]
  );

  const handleSubmenuToggle = (index) => {
    setOpenSubmenu((prev) => (prev === index ? null : index));
  };

  useEffect(() => {
    visibleNavItems.forEach((nav, index) => {
      if (nav.subItems) {
        nav.subItems.forEach((subItem) => {
          if (isActive(subItem.path)) setOpenSubmenu(index);
        });
      }
    });
  }, [location, isActive, visibleNavItems]);

  useEffect(() => {
    if (openSubmenu !== null) {
      const el = subMenuRefs.current[openSubmenu];
      setSubMenuHeight((prev) => ({
        ...prev,
        [openSubmenu]: el?.scrollHeight || 0,
      }));
    }
  }, [openSubmenu]);

  const renderMenuItems = (items) =>
    items.map((nav, index) => (
      <li key={nav.name} className="mb-2">
        {nav.subItems ? (
          <button
            onClick={() => handleSubmenuToggle(index)}
            className={`flex items-center gap-3 py-2 px-3 w-full ${
              openSubmenu === index
                ? "text-[#E5502E] font-semibold rounded-lg bg-gray-100"
                : ""
            } cursor-pointer transition-all duration-200`}
          >
            <span>{nav.icon}</span>
            {(isExpanded || isHovered || isMobileOpen) && (
              <>
                <span className="text-sm">{nav.name}</span>
                <ChevronDown
                  className={`ml-auto w-4 h-4 ${
                    openSubmenu === index ? "rotate-180" : ""
                  }`}
                />
              </>
            )}
          </button>
        ) : (
          <Link
            to={nav.path}
            className={`flex items-center gap-3 py-2 px-3 w-full rounded-lg transition-all duration-200 ${
              isActive(nav.path)
                ? "bg-[#E5502E] text-white"
                : "hover:bg-gray-100"
            }`}
          >
            <span>{nav.icon}</span>
            {(isExpanded || isHovered || isMobileOpen) && (
              <span className="text-sm">{nav.name}</span>
            )}
          </Link>
        )}

        {nav.subItems && (isExpanded || isHovered || isMobileOpen) && (
          <div
            ref={(el) => (subMenuRefs.current[index] = el)}
            style={{
              height: openSubmenu === index ? `${subMenuHeight[index]}px` : "0px",
              overflow: "hidden",
              transition: "height 0.3s",
            }}
            className="ml-4 mt-1"
          >
            <ul className="flex list-none flex-col gap-1 p-0 m-0">
              {nav.subItems.map((subItem) => (
                <li key={subItem.name}>
                  <Link
                    to={subItem.path}
                    className={`block px-3 py-1 rounded-lg text-sm transition-all duration-200 ${
                      isActive(subItem.path)
                        ? "bg-[#E5502E] text-white"
                        : "hover:bg-gray-100"
                    }`}
                  >
                    {subItem.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        )}
      </li>
    ));

  return (
    <aside
      className={`fixed flex flex-col top-16 lg:top-0 left-0 px-4 pt-4 bg-white text-gray-900 h-[calc(100vh-4rem)] lg:h-screen transition-all duration-300 ease-in-out z-[60] lg:z-50 border-r border-gray-200 
        ${isExpanded || isMobileOpen ? "w-[290px]" : isHovered ? "w-[290px]" : "w-[90px]"}
        ${isMobileOpen ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0`}
      onMouseEnter={() => !isExpanded && setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Logo */}
      <div
        className={`py-4 flex ${
          !isExpanded && !isHovered ? "lg:justify-center" : "justify-start"
        }`}
      >
        <Link to="/">
          <img
            src={process.env.PUBLIC_URL + "/images/logo.png"}
            alt="Logo"
            width={isExpanded || isHovered || isMobileOpen ? 140 : 28}
            height={36}
          />
        </Link>
      </div>

      {/* Barre de recherche */}
      <div className="px-2 mb-6">
        <input
          type="text"
          placeholder="Rechercher..."
          className="w-full h-10 px-2 text-sm border border-[#E8E8E9] rounded-lg bg-transparent focus:outline-none focus:ring-2 focus:ring-orange-500"
        />
      </div>

      {/* Menu */}
      <div className="flex flex-col overflow-y-auto duration-300 ease-linear no-scrollbar">
        <nav>
          {isLoading ? (
            <div className="px-3 py-2 text-sm text-gray-400">Chargement…</div>
          ) : visibleNavItems.length > 0 ? (
            <ul className="list-none p-0 m-0">{renderMenuItems(visibleNavItems)}</ul>
          ) : (
            <div className="px-3 py-2 text-sm text-gray-400">
              Aucun accès disponible
            </div>
          )}
        </nav>
      </div>
    </aside>
  );
};

export default AdminSidebar;
